import React, { useState, useEffect } from "react";
import { authFetch } from "../pages/Auth";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  COLORS, CHART_COLORS, RISK_COLORS, commonAxisStyle, commonGridStyle,
  tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(path)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);
  return { data, loading, error };
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`,
        borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function KPICard({ label, value, sub, color, icon }) {
  return (
    <div style={{ ...cardStyle, position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: color || COLORS.primary, borderRadius: "10px 10px 0 0",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || COLORS.text, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={chartHeaderStyle}>{title}</div>
      {children}
    </div>
  );
}

export default function MaintenancePhase() {
  const adherence = useApi("/api/analytics/adherence");
  const dropout = useApi("/api/ml/dropout-risk");
  const segments = useApi("/api/ml/segments");

  const isLoading = adherence.loading || dropout.loading || segments.loading;
  if (isLoading) return <Spinner />;

  const adh = adherence.data || {};
  const drop = dropout.data || {};
  const seg = segments.data || {};

  // Adherence KPIs
  const avgPDC = adh.average_pdc || adh.avgPdc || adh.avg_pdc || null;
  const refillData = adh.by_program || adh.refill_trends || [];

  // Dropout risk breakdown
  const riskPatients = Array.isArray(drop) ? drop : (drop.patients || drop.risk_scores || []);
  const highRisk = riskPatients.filter((p) => (p.risk_level || p.riskLevel) === "high").length;
  const medRisk = riskPatients.filter((p) => (p.risk_level || p.riskLevel) === "medium").length;
  const lowRisk = riskPatients.filter((p) => (p.risk_level || p.riskLevel) === "low").length;
  const totalRisk = highRisk + medRisk + lowRisk;

  const riskPieData = [
    { name: "High", value: highRisk, color: RISK_COLORS.high },
    { name: "Medium", value: medRisk, color: RISK_COLORS.medium },
    { name: "Low", value: lowRisk, color: RISK_COLORS.low },
  ].filter((d) => d.value > 0);

  // Segments
  const segmentList = Array.isArray(seg) ? seg : (seg.segments || []);

  // Retention
  const retentionRate = adh.retention_rate || adh.retentionRate || null;
  const activeCount = adh.active_patients || adh.activePatients || null;

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KPICard
          label="Average PDC"
          value={avgPDC != null ? `${Math.round(avgPDC)}%` : "N/A"}
          sub="Proportion of days covered"
          color={COLORS.primary}
          icon="💊"
        />
        <KPICard
          label="High-Risk Patients"
          value={highRisk}
          sub={totalRisk > 0 ? `${Math.round((highRisk / totalRisk) * 100)}% of assessed` : "From dropout model"}
          color={RISK_COLORS.high}
          icon="⚠"
        />
        <KPICard
          label="Retention Rate"
          value={retentionRate != null ? `${retentionRate}%` : (activeCount != null ? activeCount.toLocaleString() : "N/A")}
          sub={retentionRate != null ? "Currently active" : "Active patients"}
          color={COLORS.success}
          icon="📈"
        />
      </div>

      {/* Dropout Risk Heatmap */}
      <SectionCard title="Dropout Risk Distribution">
        {riskPieData.length > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
            <ResponsiveContainer width="50%" height={220}>
              <PieChart>
                <Pie data={riskPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" paddingAngle={3}>
                  {riskPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {riskPieData.map((d) => (
                <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: d.color }} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 600, minWidth: 60 }}>{d.name}</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: d.color }}>{d.value}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>patients</span>
                </div>
              ))}
              <div style={{ marginTop: 8, fontSize: 11, color: COLORS.muted }}>
                Total assessed: {totalRisk.toLocaleString()}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Run ML training to generate dropout risk scores
          </div>
        )}
      </SectionCard>

      {/* Adherence by Program */}
      <SectionCard title="Adherence by Program">
        {Array.isArray(refillData) && refillData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={refillData}>
              <CartesianGrid {...commonGridStyle} />
              <XAxis dataKey="program" tick={commonAxisStyle} />
              <YAxis tick={commonAxisStyle} domain={[0, 100]} unit="%" />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="pdc" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} name="PDC %" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Adherence data unavailable
          </div>
        )}
      </SectionCard>

      {/* Patient Segments */}
      <SectionCard title="Patient Segments">
        {segmentList.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            {segmentList.map((seg, i) => (
              <div key={i} style={{
                background: COLORS.bgDark, borderRadius: 8, padding: 14,
                border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                  {seg.segment_name || seg.name || `Segment ${i + 1}`}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: CHART_COLORS[i % CHART_COLORS.length], marginBottom: 4 }}>
                  {(seg.patient_count || seg.count || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>
                  {seg.description || seg.characteristics || ""}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Run ML segmentation to identify patient clusters
          </div>
        )}
      </SectionCard>
    </div>
  );
}
